import { useState } from 'react';
import { motion } from 'motion/react';
import { Calendar, Plus, Minus } from 'lucide-react';
import { experienceTimeline } from '../data';
import useIsMobile from '../hooks/useIsMobile';
import jahangirImg from '../assets/images/regenerated_image_1780102721093.webp';

export default function ExperienceTimeline() {
  const [expandedId, setExpandedId] = useState<string | null>('exp-0');
  const isMobile = useIsMobile();

  const toggleExpand = (id: string) => {
    setExpandedId((prevId) => (prevId === id ? null : id));
  };
  return (
    <section id="about" className="py-24 scroll-mt-24 relative overflow-hidden bg-transparent text-neutral-900 dark:text-white transition-colors duration-500">
      {/* Background Ambience / Blur Overlays replicating premium high-end portfolio layout */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Left Column Profile Bio and details */}
          <motion.div 
            initial={isMobile ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: -70, scale: 0.96 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: false, amount: 0.1 }}
            transition={{ type: "spring", stiffness: 60, damping: 14 }}
            className="lg:col-span-5 space-y-8"
          >
            <div>
              <h2 className="text-2xl sm:text-4xl font-sans tracking-tight text-neutral-900 dark:text-white font-medium hover:text-emerald-500 hover:scale-[1.015] transform origin-left transition-all duration-300 cursor-default">
                Jahangir's Story
              </h2>
            </div>

            <div className="relative h-80 sm:h-96 w-full rounded-2xl overflow-hidden group shadow-xl">
              <img
                src={jahangirImg}
                alt="Jahangir inside studio"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>

            <div className="space-y-6 text-sm sm:text-base text-neutral-500 dark:text-neutral-400 font-sans leading-relaxed font-light">
              <p>
                Design escapes the screen. For Jahangir, it’s an active dialogue and an ongoing experiment. His process goes beyond pixels—steps on-site, decode the environment, and uncover the true need before he ever starts building. Design isn’t just what he sees; it’s a way of living.
              </p>
              <p>
                From Jahangir's Kashmiri roots and upbringing abroad to his career in Lahore, his work spans all over Pakistan. Off-screen, he's a multidisciplinary musician who loves crafting sounds and traveling the north.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Timeline career heights */}
          <div className="lg:col-span-7 space-y-12">
            <div>
              <span className="font-mono text-[10px] tracking-[0.4em] text-emerald-500 font-extrabold uppercase block mb-4">
                CAREER TIMELINE
              </span>
            </div>

            {/* Vertical guide line line inside container */}
            <div className="relative border-l border-neutral-200 dark:border-neutral-800 pl-6 sm:pl-8 space-y-4">
              {experienceTimeline.map((exp, idx) => {
                const isExpanded = exp.id === expandedId;
                return (
                  <motion.div
                    key={exp.id}
                    initial={isMobile ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: -55, scale: 0.96 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: false, margin: '-5% 0px -5% 0px' }}
                    transition={isMobile ? { duration: 0.2 } : { type: "spring", stiffness: 70, damping: 13, delay: Math.min(idx * 0.08, 0.4) }}
                    className="relative group pr-4"
                  >
                    {/* Glowing vertical point bullet joint */}
                    <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-white dark:bg-neutral-950 border-2 border-neutral-300 dark:border-neutral-700 group-hover:border-emerald-500 group-hover:bg-emerald-500 transition-all duration-300 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-neutral-200 dark:bg-neutral-800 group-hover:bg-white" />
                    </div>

                    <div
                      onClick={() => toggleExpand(exp.id)}
                      className="cursor-pointer select-none group/title focus:outline-none w-full text-left rounded-xl p-3 sm:p-4 -mt-3 -ml-3 sm:-ml-4 transition-all duration-300 hover:bg-neutral-50/50 dark:hover:bg-neutral-900/10 border border-transparent hover:border-neutral-150/10 dark:hover:border-neutral-800/10"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          toggleExpand(exp.id);
                        }
                      }}
                    >
                      {/* Header box */}
                      <div className="flex items-center justify-between gap-2 mb-2 pr-1">
                        <span className="font-mono text-[10px] sm:text-xs font-bold text-neutral-500 dark:text-neutral-400 flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-emerald-500" /> {exp.year}
                        </span>
                        <div className={`flex items-center justify-center w-5 h-5 rounded-full border transition-all duration-300 ${
                          isExpanded
                            ? 'border-emerald-500 text-emerald-500 bg-emerald-500/15 dark:bg-emerald-500/20'
                            : 'border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/30 group-hover/title:text-emerald-500 group-hover/title:border-emerald-500/30'
                        }`}>
                          {isExpanded ? (
                            <Minus className="w-3 h-3" />
                          ) : (
                            <Plus className="w-3 h-3" />
                          )}
                        </div>
                      </div>

                      {/* Job Title / Role */}
                      <h4 className={`text-lg sm:text-xl font-sans font-semibold tracking-tight flex flex-wrap items-center gap-x-3 gap-y-1.5 transition-all duration-300 transform origin-left group-hover/title:scale-[1.015] ${
                        isExpanded
                          ? 'text-emerald-500 dark:text-emerald-400'
                          : 'text-neutral-900 dark:text-white hover:text-emerald-500 group-hover/title:text-emerald-500'
                      }`}>
                        <span>{exp.role}</span>
                        {exp.companies && exp.companies.length > 0 ? (
                          exp.companies.map((c, i) => (
                            <span key={i} className="font-mono text-[9px] font-bold text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-800 px-2 py-0.5 rounded uppercase tracking-wider bg-neutral-50 dark:bg-neutral-900/50">
                              {c}
                            </span>
                          ))
                        ) : (
                          exp.company && (
                            <span className="font-mono text-[9px] font-bold text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-800 px-2 py-0.5 rounded uppercase tracking-wider bg-neutral-50 dark:bg-neutral-900/50">
                              {exp.company}
                            </span>
                          )
                        )}
                      </h4>

                      {/* Expandable detailed content using motion */}
                      <motion.div
                        initial={false}
                        animate={{
                          height: isExpanded ? 'auto' : 0,
                          opacity: isExpanded ? 1 : 0,
                        }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 space-y-4">
                          <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 font-sans leading-relaxed">
                            {exp.description}
                          </p>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
